import React, { useEffect, useState } from 'react'
import { isNil } from 'lodash'
import { differenceInDays } from 'date-fns'

import { Button } from '@/components/buttons/Button'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { AddToPlaylist } from '@/views/Playlists/components/AddToPlaylist'
import { BulkAssetExport } from '@/views/Calls/components/BulkAssetExport'
import { OmnichannelPromotionButton } from '@/components/promotions/OmnichannelPromotionButton'
import { CHANNEL_NAME, CHANNEL_NAME_PLURAL, CHANNEL_TYPE } from '@/utils/constants'
import { hasChannelAccess, shouldShowOmniPromotion } from '@/utils/helpers'
import { enrichCallsWithTagCategories } from '@/reducers/customColumns/customColumns.helpers'
import { filterCallsData } from '@/views/Calls/components/helpers'
import rocketIcon from '@/assets/images/rocket.svg'

export const CallSearchTable = ({
  columns,
  filters,
  calls,
  loading,
  handleClearFilters,
  updateRowIdsRendered,
  organizationId,
  orgActiveChannels,
  tagCategoriesData = [],
  isBaltoAdmin,
  onSearch,
  onSearchOpen,
  loadingSearchAccessors,
}) => {
  const [selectedRows, setSelectedRows] = useState([])
  const isEmailChannel =
    hasChannelAccess(orgActiveChannels, CHANNEL_TYPE.EMAIL, isBaltoAdmin) &&
    filters.channel === CHANNEL_TYPE.EMAIL

  const handleBulkSelectSingle = (row) => {
    if (!selectedRows.includes(row)) {
      setSelectedRows([...selectedRows, row])
    } else {
      setSelectedRows(selectedRows.filter((selectedRow) => selectedRow !== row))
    }
  }

  const handleSelectAll = (rows) => {
    setSelectedRows(rows.map((row) => row.call_id))
  }

  const handleDeselectAll = () => {
    setSelectedRows([])
  }

  useEffect(() => {
    // Deselect all filters if loading, because that means a new call search is being performed
    if (loading.calls) {
      handleDeselectAll()
    }
  }, [loading])

  const parseScoresData = (callsData) => {
    return callsData.map((call) => {
      const updatedCall = { ...call }

      call.scorecards?.forEach((scorecard) => {
        // to match redacted and unredacted data
        filters.scorecards.forEach((_scorecard) => {
          if (scorecard.scorecard_sid === _scorecard.sid)
            scorecard.scorecard_name = _scorecard.label
        })
        updatedCall[`${scorecard.scorecard_name} - Overall QA Score`] = scorecard.qa_score
      })
      return updatedCall
    })
  }
  const formatCallsData = () => {
    const dataWithScores = parseScoresData(calls)
    const dataWithTags = enrichCallsWithTagCategories(dataWithScores, tagCategoriesData)

    const filteredCalls = filterCallsData(dataWithTags)

    // assign balto_id for each call
    return filteredCalls.map((call) => {
      const balto_id =
        isNil(call.record_type) || call.record_type === CHANNEL_TYPE.CALL
          ? call.call_id
          : call.message_id
      return { ...call, balto_id }
    })
  }

  const dateRangeExceeds90Days =
    filters?.startDate &&
    filters?.endDate &&
    differenceInDays(new Date(filters.endDate), new Date(filters.startDate)) >= 90

  const channel = filters?.channel || CHANNEL_TYPE.CALL
  const showOmniPromotion = shouldShowOmniPromotion(orgActiveChannels, channel, isBaltoAdmin)
  const noDataHeader = showOmniPromotion ? (
    <>
      <img alt="Rocket icon - omni-channel promotion" className="mb" src={rocketIcon} />
      <div>{`Balto now supports ${CHANNEL_NAME[channel]} conversations!`}</div>
    </>
  ) : (
    `No ${CHANNEL_NAME_PLURAL[channel]} match the search criteria`
  )
  const noDataText = showOmniPromotion ? (
    <>
      {`View, analyze, and coach customer ${CHANNEL_NAME_PLURAL[channel]} right alongside your calls - all in one place.`}
      <br />
      Ask your admin to enable Omnichannel to get started
      <OmnichannelPromotionButton />
    </>
  ) : (
    'Try increasing the date range or modifying your current filters.'
  )

  return (
    <AdvancedTable
      dataGrid
      columns={columns}
      rows={showOmniPromotion ? [] : formatCallsData()}
      loading={loading.calls}
      loadingText={
        loading.calls && dateRangeExceeds90Days
          ? 'Searching a large date range, this may take a moment...'
          : undefined
      }
      noDataHeader={noDataHeader}
      noDataText={noDataText}
      noDataAction={
        !!filters.organizationId &&
        !showOmniPromotion && <Button onClick={handleClearFilters}>Clear All Filters</Button>
      }
      rowsPerPage={30}
      pagination
      index="call_id"
      defaultOrder="desc"
      defaultOrderBy="start_time"
      bulkSelectable
      selectedRows={selectedRows}
      handleBulkSelectSingle={handleBulkSelectSingle}
      handleDeselectAll={handleDeselectAll}
      bulkSelectAction={
        organizationId && (
          // Wrapped rather than left as siblings: the banner is
          // space-between, so two bare children would be pushed to opposite
          // ends of it instead of sitting together on the right.
          <div className="flex-align-center small-gap">
            <AddToPlaylist
              organizationId={organizationId}
              callIds={selectedRows}
              handleDeselectAll={handleDeselectAll}
            />
            {/* Calls only. Email, SMS and chat rows carry neither audio nor
                a postcall transcript -- an export of them would come back as
                a manifest of failures. */}
            {channel === CHANNEL_TYPE.CALL && (
              <BulkAssetExport callIds={selectedRows} organizationId={organizationId} />
            )}
          </div>
        )
      }
      handleSelectAll={handleSelectAll}
      updateRowIdsRendered={updateRowIdsRendered}
      onSearch={onSearch}
      onSearchOpen={onSearchOpen}
      loadingSearchAccessors={loadingSearchAccessors}
      itemName={CHANNEL_NAME[channel]}
      bottomCaption={isEmailChannel && 'Attachment-only emails are excluded from this view.'}
    />
  )
}
